import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { accountBalanceSlice, userSecuritiesAccountsSlice } from '!core/redux';
import {
  accountBalanceSelectedSelector,
  getAccountBalance,
} from '!core/redux/accountBalance/accountBalance.selectors';
import {
  securitiesAccountsSelector,
  securityAccountSelectedSelector,
} from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { TrlDropdown } from '»components/atoms/Inputs';
import { insertOrderPrimarySelector } from '»components/templates';
import { TrlFormattedNumberPlain } from '»utils/i18n';
import { TrlNumberFormatted } from '»utils/number';
import { trlFieldDefaultValue } from '»utils/trlFieldDefaultValue';

import {
  AccountContainerStyled,
  BalanceContainerStyled,
  BoldText,
  DropdownContainer,
  LabelStyled,
  NumberStyled,
  SectionStyled,
  SelectorsStyled,
  SpanStyled,
  TradingBalanceStyled,
} from './TrlAccountInfo.styles';

export const TrlAccountInfo = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const accountsBalance = useSelector(getAccountBalance);
  const disableSelection = useSelector(insertOrderPrimarySelector);
  const currentSecurityAccount = useSelector(securityAccountSelectedSelector);
  const currentBalanceAccount = useSelector(accountBalanceSelectedSelector);

  const balance = accountsBalance.find(
    (account) => account.value === currentBalanceAccount,
  )?.balance;

  const currentCurrency = accountsBalance.find(
    (account) => account.value === currentBalanceAccount,
  )?.currency;

  const onChangeSecurityAccount = (e) => {
    dispatch(
      userSecuritiesAccountsSlice.actions.setSelectedAccount(e.target.value),
    );
    dispatch(
      accountBalanceSlice.actions.accountBalanceRequest({
        securityAccount: e.target.value,
      }),
    );
  };
  const onChangeCashAccount = (e) => {
    dispatch(accountBalanceSlice.actions.setSelectedAccount(e.target.value));
  };

  const securityAccountHasChanged = !accountsBalance?.find(
    (el) => el.value === currentBalanceAccount,
  );

  useEffect(() => {
    if (accountsBalance.length > 0) {
      if (!currentBalanceAccount) {
        const firstCashAccountNumber = accountsBalance?.[0]?.value ?? null;

        if (currentBalanceAccount !== firstCashAccountNumber)
          dispatch(
            accountBalanceSlice.actions.setSelectedAccount(
              firstCashAccountNumber ?? null,
            ),
          );
      } else {
        const selectedCashAccount = accountsBalance.find(
          (el) => el.value === currentBalanceAccount,
        );

        if (securityAccountHasChanged) {
          const firstCashAccountNumber =
            selectedCashAccount?.value ?? accountsBalance?.[0]?.value ?? null;

          if (currentBalanceAccount !== firstCashAccountNumber)
            dispatch(
              accountBalanceSlice.actions.setSelectedAccount(
                firstCashAccountNumber ?? null,
              ),
            );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsBalance]);

  return (
    <AccountContainerStyled>
      <SelectorsStyled>
        <SectionStyled>
          <SpanStyled disabled={disableSelection}>
            <FormattedMessage id="TRLT_UI-CUSTODIAN_ACCOUNT" />
          </SpanStyled>
          <DropdownContainer>
            <TrlDropdown
              name="account"
              value={currentSecurityAccount}
              options={useSelector(securitiesAccountsSelector)}
              onChange={onChangeSecurityAccount}
              disabled={disableSelection}
            />
          </DropdownContainer>
        </SectionStyled>

        <SectionStyled>
          <SpanStyled disabled={disableSelection}>
            <FormattedMessage id="TRLT_UI-CURRENT_ACCOUNT" />
          </SpanStyled>

          <DropdownContainer>
            <TrlDropdown
              name="accounts"
              value={currentBalanceAccount}
              options={accountsBalance}
              onChange={onChangeCashAccount}
              disabled={disableSelection || !currentBalanceAccount}
            />
          </DropdownContainer>
        </SectionStyled>
        {!matches && (
          <SectionStyled>
            <SpanStyled>
              <FormattedMessage id="TRLT_UI-TRADING_BALANCE" />
            </SpanStyled>
            <BalanceContainerStyled>
              <NumberStyled>
                <TrlNumberFormatted value={balance} decimalDigits={2} />
              </NumberStyled>
            </BalanceContainerStyled>
          </SectionStyled>
        )}
      </SelectorsStyled>
      {matches && (
        <TradingBalanceStyled>
          <LabelStyled>
            <FormattedMessage id="TRLT_UI-TRADING_BALANCE" />
          </LabelStyled>
          <BoldText id="currentBalanceAccountValue">
            {!balance || balance === trlFieldDefaultValue()
              ? trlFieldDefaultValue()
              : TrlFormattedNumberPlain(balance, 2)}
          </BoldText>{' '}
          <BoldText>{currentCurrency}</BoldText>
        </TradingBalanceStyled>
      )}
    </AccountContainerStyled>
  );
};
